import { html, LitElement, nothing, type PropertyValues, unsafeCSS } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { createRef, type Ref } from 'lit/directives/ref.js';
import { I18n } from '../../../_ui-core_/base/package/services/package/i18n/i18n.de';
import { handleKeyboardTrigger } from '../../../_ui-core_/base/package/util/a11y';
import { dispatchCustomEvent } from '../../../_ui-core_/base/package/util/event';
import { resolveAndRasterizeGameAssetUrl } from '../../../_ui-core_/base/package/util/game-asset-url';
import type { UiGamesDrawer } from '../../../_ui-core_/components/ui-games-drawer/ui-games-drawer';
import { showGamificationButton } from '../../../_ui-core_/mod-incentive/helper/gamification-launcher';
import type {
  ContributionTarget,
  ContributionTournamentTarget,
  MissionState,
  TournamentState,
} from '../../../_ui-core_/mod-incentive/types';
import App from '../../../app';
import style from './game-page-desktop-fullscreen.css?inline';
import '../../../_ui-core_/components/icons/ui-info-icon';
import '../../../_ui-core_/components/incentive/mission-bar/mission-bar';
import '../../../_ui-core_/components/incentive/tournament-bar/tournament-bar';
import '../../../_ui-core_/components/ui-game-navigation-desktop/ui-game-navigation-desktop';
import '../../../_ui-core_/components/ui-header/ui-header-icon-user';
import '../../../_ui-core_/components/ui-lugas-session-timer/ui-lugas-session-timer';
import '../../../_ui-core_/components/ui-panic-button/ui-panic-button';

const CName = 'game-page-desktop-fullscreen';

/**
 * @fires showPanicInfo - When the panic info icon is clicked
 */
@customElement(CName)
export class GamePageDesktopFullScreen extends LitElement {
  static readonly styles = unsafeCSS(style);

  @property({ type: Boolean }) forReal: boolean;
  @property({ type: String }) gameId: string;
  @property({ type: String }) gameName: string;
  @property({ type: String }) gameSlug: string;
  @property({ type: Object }) incentiveMissionTarget: ContributionTarget | null;
  @property({ type: Object }) incentiveMissionState?: MissionState;
  @property({ type: Object }) incentiveTournamentTarget: ContributionTournamentTarget | null;
  @property({ type: Object }) incentiveTournamentState?: TournamentState;

  @state() private _mainBackground = 'transparent';
  @state() private _shouldRenderGamificationIcon = false;

  @query('.game-view') private readonly _gameSection?: HTMLElement;
  @query('#info-bar') private readonly _infoBar?: HTMLDivElement;
  @query('#game-navigation') private readonly _gameNavigation?: LitElement;

  private _sizesSet: boolean[] = [false, false];

  private readonly _drawerRef: Ref<UiGamesDrawer> = createRef();
  private readonly _boundSetGameSectionHeightOnSafari = this._setGameSectionHeightOnSafari.bind(this);

  private _abortController: AbortController | null = null;
  private _resizeObserver: ResizeObserver | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    this._abortController = new AbortController();
    window.addEventListener('resize', this._boundSetGameSectionHeightOnSafari);
    const signal = this._abortController.signal;
    if (!signal.aborted) {
      this._shouldRenderGamificationIcon = showGamificationButton();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this._boundSetGameSectionHeightOnSafari);
    this._abortController?.abort();
    this._abortController = null;
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
  }

  firstUpdated(): void {
    this._setGameSectionHeightOnSafari();
    this._setupInfoBarPanel();
  }

  async updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    const signal = this._abortController?.signal;

    const isStartupOK = await App.instance.checkPlay(this.forReal, this.gameId);
    if (signal?.aborted) return;
    if (!isStartupOK) {
      window.$app.logger.log('startup check failed => redirect to home');
      App.instance.router.navigateToHome();
    }

    if (changedProperties.has('gameId')) {
      this._getGameInfo(this.gameId);
      this._drawerRef.value?.close();
    }
  }

  render() {
    const isTournamentProgressShown = !!this.incentiveTournamentTarget && this.incentiveTournamentState?.hasStarted;
    const isMissionProgressShown = !!this.incentiveMissionTarget;
    const infoBarClasses = classMap({
      'info-bar': true,
      'manual-sizing': this._sizesSet.every((e) => e),
      'with-mission-bar': isTournamentProgressShown || isMissionProgressShown,
    });

    return html`
      <main class="desktop-game-page fullscreen" data-id=${this.gameId}>
        <div class="header-bar">
          <ui-panic-button
            .secondary="${false}"
            .pressDurationS=${3}
            .text=${window.$app.$t.get(I18n.gamePage.panicButton)}
          ></ui-panic-button>
          <ui-info-icon tabindex="0" @click=${this._handlePanicInfoClick} @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._handlePanicInfoClick.bind(this))}></ui-info-icon>
          ${this._renderAccountButton()}
        </div>
        <div class="main">
          <ui-game-view
            class="game-view"
            .forReal=${this.forReal}
            .gameId=${this.gameId}
            .gameName=${this.gameName}
            .gameSlug=${this.gameSlug}
            .incentiveMission=${this.incentiveMissionTarget}
          ></ui-game-view>
          <div id="info-bar" class=${infoBarClasses}>
            <div class="mission-bar-panel">
              ${this._renderProgressBar()}
            </div>
            <ui-game-navigation-desktop id="game-navigation" .shouldShowGamificationIcon=${this._shouldRenderGamificationIcon}></ui-game-navigation-desktop>
          </div>
        </div>
        <div class="lugas-timer-panel">
          <ui-lugas-session-timer></ui-lugas-session-timer>
        </div>
      </main>
    `;
  }

  private _renderAccountButton() {
    return html`
        <ui-header-icon-user
          @click=${() => this._goToAccount()}
          @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._goToAccount)}
          aria-label=${window.$app.$t.get(I18n.base.account)}
          class="account-button"
          role="button"
          tabindex="0"
        ></ui-header-icon-user>
      `;
  }

  private _renderProgressBar() {
    if (this.incentiveMissionTarget) {
      return html`
        <incentive-mission-bar
          .bundleId=${this.incentiveMissionTarget.bundleId}
          .expirationDate=${this.incentiveMissionTarget.endDate}
          .reward=${this.incentiveMissionTarget.reward}
          .progress=${this.incentiveMissionState?.progress}
          .remaining=${this.incentiveMissionState?.remaining}
          .isExpired=${this.incentiveMissionState?.isExpired}
          .isDesktop=${true}
          class="mission-bar mission-bar--gamanza"
        ></incentive-mission-bar>`;
    }

    if (this.incentiveTournamentTarget && this.incentiveTournamentState?.hasStarted) {
      return html`
        <incentive-tournament-bar
          .tournamentId=${this.incentiveTournamentTarget.tournamentId}
          .expirationDate=${this.incentiveTournamentTarget.endDate}
          .position=${this.incentiveTournamentState?.userPosition}
          .points=${this.incentiveTournamentState?.points}
          .joinedAmount=${this.incentiveTournamentState?.joinedAmount}
          .isEnded=${this.incentiveTournamentState?.hasEnded}
          .areResultsReady=${this.incentiveTournamentState?.areResultsReady}
          .isDesktop=${true}
          class="mission-bar mission-bar--gamanza"
        ></incentive-tournament-bar>`;
    }

    return nothing;
  }

  private _getGameInfo(gameId: string) {
    const signal = this._abortController?.signal;
    App.instance.content
      .getGameInfo(gameId)
      .then(async (gameInfo) => {
        if (signal?.aborted) return;
        const img = gameInfo.img ?? null;
        this._mainBackground = img ? await resolveAndRasterizeGameAssetUrl(img) : 'transparent';
        if (signal?.aborted) return;
        this.style.setProperty('--_gameAsset', this._mainBackground);
      })
      .catch((err) => {
        if (signal?.aborted) return;
        window.$app.logger.warn(`Error fetching game info '${this.gameId}'`, err);
      });
  }

  private _setupInfoBarPanel() {
    this._resizeObserver = new ResizeObserver(() => {
      if (this._infoBar?.clientWidth) {
        this.style.setProperty('--_infoBarWidth', `${this._infoBar.clientWidth}px`);
        this.style.setProperty('--_infoBarHeight', `${this._infoBar.clientHeight}px`);
        this._sizesSet[0] = true;
      }
      if (this._gameNavigation?.clientWidth) {
        this.style.setProperty('--_navigationWidth', `${this._gameNavigation.clientWidth}px`);
        this._sizesSet[1] = true;
      }
    });
    if (this._infoBar) {
      this._resizeObserver.observe(this._infoBar);
    }
    if (this._gameNavigation) {
      this._resizeObserver.observe(this._gameNavigation);
    }
  }

  private _handlePanicInfoClick() {
    dispatchCustomEvent(this, 'showPanicInfo');
  }

  private _goToAccount() {
    App.instance.router.navigateToAccount();
  }

  private _setGameSectionHeightOnSafari() {
    if (window.$app.device.browserType === 'safari' && this._gameSection) {
      this._gameSection.style.height = '';
      this._gameSection.style.height = `${this._gameSection.offsetHeight}px`;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: GamePageDesktopFullScreen;
  }
}
